import React from 'react';
import cx from 'classnames';

import './styles/Grid.scss';

export type GapSize =
  | 'xxxxlarge'
  | 'xxxlarge'
  | 'xxlarge'
  | 'xlarge'
  | 'large'
  | 'medium'
  | 'small'
  | 'xsmall'
  | 'xxsmall'
  | 'xxxsmall'
  | 'xxxxsmall'
  | 'xxxxxsmall';

type RowsOrColumnsProp = string | number | [number, string];

type Props<P> = {
  as?: React.ComponentType<P> | React.ElementType;
  columns?: RowsOrColumnsProp;
  rows?: RowsOrColumnsProp;
  areas?: string[];
  gap?: GapSize;
  rowGap?: GapSize;
  columnGap?: GapSize;
  placeContent?: string;
  placeItems?: string;
  inline?: boolean;
  children?: React.ReactNode;
  className?: string;
} & Partial<P>;

const formatRowsOrColumns = (prop?: RowsOrColumnsProp) => {
  if (!prop || typeof prop === 'string') return prop;
  const [qty, size] = Array.isArray(prop) ? prop : [prop, '1fr'];
  return `repeat(${qty}, ${size})`;
};

const formatAreas = (areas: string[]) => areas.map(a => `"${a}"`).join(' ');

/**
 * Component that exposes the most useful parts of the CSS grid API as React props.
 * Supports grid-template-rows, grid-template-columns, grid-template-areas (as`rows`,
 * `columns`, and `areas` props respectively), inline grids via the `inline` prop,
 * column and row gaps (`columnGap` and `rowGap` respectively, or `gap` to set both),
 * and the alignment shorthands `placeItems` and `placeContent`.
 *
 * Grid also supports rendering as another component or element via the `as` prop, and
 * is typed to accept that component's props as well, though the component MUST support
 * `style` and `className` props in order for Grid to work.
 *
 * The `rows` and `columns` props accept 3 different signatures:
 *
 * Prop value  | grid-template-* CSS
 * ------------|---------------------
 * "80%, 2fr"  | 80%, 2fr;
 * 3           | repeat(3, 1fr);
 * [3, 'auto'] | repeat(3, auto);
 *
 * The `placeItems` and `placeContent` props are converted directly to place-items and
 * place-content CSS rules. The place-* rules are shorthands to set align-* and justify-*,
 * where the first value is align (vertical) and the second is justify (horizontal). This
 * doc gives a good overview of the effects of align-items, justify-items, align-content,
 * and justify-content: https://www.digitalocean.com/community/tutorials/css-align-justify
 *
 * The `rowGap`, `columnGap` and `gap` props accept a string representing a spacing value,
 * from `xxxxsmall` up to `xxxxlarge`.
 *
 * The `areas` prop takes an array of strings, where each string represents a row of the
 * grid, and is converted directly to the grid-template-areas CSS rule. Used in tandem
 * with GridItem, grid areas allow us to place elements in a specific grid cell regardless
 * of the element's position among the grid's children.
 *
 * ### Examples
 *
 * Baisc two-column grid with dynamic content and evenly sized columns:
 *
 * ```tsx
 * <Grid columns={2} columnGap="medium">
 *   {data.map({ label, value }) => (
 *     <Text weight="semibold">{label}</Text>
 *     <Text>{value}</Text>
 *   )}
 * </Grid>
 * ```
 *
 * Using grid areas:
 *
 * ```tsx
 * <Grid
 *   areas={['header header', 'sidebar content', 'footer footer']}
 *   columns="20% auto"
 *   rows={[3, auto]}
 * >
 *   <GridItem as={Heading} area="header">{title}</GridItem>
 *   <GridItem area="footer">{renderFooterStuff()}</GridItem>
 *   <GridItem area="sidebar">{renderSidebar()}</GridItem>
 *   <GridItem area="content">{renderContent()}</GridItem>
 * </Grid>
 * ```
 */
export default function Grid<P>({
  as: Component = 'div',
  columns,
  rows,
  areas,
  gap,
  rowGap,
  columnGap,
  placeContent,
  placeItems,
  inline = false,
  className,
  ...props
}: Props<P>) {
  return (
    <Component
      className={cx(
        'Grid',
        (rowGap || gap) && `Grid--rowGap--${rowGap || gap}`,
        (columnGap || gap) && `Grid--columnGap--${columnGap || gap}`,
        inline && `Grid--inline`,
        className
      )}
      style={{
        gridTemplateColumns: formatRowsOrColumns(columns),
        gridTemplateRows: formatRowsOrColumns(rows),
        ...(areas && { gridTemplateAreas: formatAreas(areas) }),
        placeContent,
        placeItems,
      }}
      {...props}
    />
  );
}
